import { useRef, useState } from "react";
import { Bell, CircleHelp, MessageSquareText } from "lucide-react";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Typography,
} from "@/components/ui";
import { GlobalMatterSearch } from "@/components/ui/AppTopBar/GlobalMatterSearch";
import { NotificationItems } from "@/components/ui/NotificationItems/NotificationItems";
import { useLiveNotificationToasts } from "@/components/ui/NotificationItems/useLiveNotificationToasts";
import { type NotificationCategory } from "@/components/ui/NotificationItems/notificationCategories";
import { UserProfileDropdown } from "@/components/ui/UserProfileDropdown/UserProfileDropdown";
import {
  getNotificationInboxItemsOptions,
  useNotificationInboxStream,
} from "@/lib/data/react-query";
import { useQuery } from "@tanstack/react-query";

type NotificationPopoverType = Exclude<NotificationCategory, "all">;

const formatUnreadCount = (count: number, isLoading: boolean) =>
  isLoading ? "…" : count > 99 ? "99+" : count;

export function AppTopBar() {
  const showLiveNotifications = useLiveNotificationToasts();
  const isNotificationStreamReady = useNotificationInboxStream(
    showLiveNotifications,
  );
  const { data: unreadCountResponse, isLoading: unreadCountLoading } = useQuery(
    getNotificationInboxItemsOptions({
      enabled: isNotificationStreamReady,
      filters: { unread: true },
    }),
  );
  const communicationUnreadCount =
    unreadCountResponse?.data.unreadCounts.communication ?? 0;
  const standardUnreadCount =
    unreadCountResponse?.data.unreadCounts.standard ?? 0;

  const [openNotificationPopover, setOpenNotificationPopover] =
    useState<NotificationPopoverType | null>(null);
  const [isNotificationContextMenuOpen, setIsNotificationContextMenuOpen] =
    useState(false);
  const notificationPopoverContentRef = useRef<HTMLDivElement>(null);

  const notificationPopovers = [
    {
      type: "communication" as const,
      label: "Communication notifications",
      title: "Communications",
      Icon: MessageSquareText,
      unreadCount: communicationUnreadCount,
    },
    {
      type: "standard" as const,
      label: "Notifications",
      title: "Notifications",
      Icon: Bell,
      unreadCount: standardUnreadCount,
    },
  ];

  return (
    <header className="bg-shell sticky top-0 z-10 flex h-14 shrink-0 items-center justify-between px-6">
      <div className="min-w-0 flex-1 pr-4">
        <div className="hidden lg:block">
          <GlobalMatterSearch />
        </div>
      </div>

      {/* Right actions */}
      <div className="flex shrink-0 items-center gap-2">
        {notificationPopovers.map(
          ({ type, label, title, Icon, unreadCount }) => {
            const formattedUnreadCount = formatUnreadCount(
              unreadCount,
              unreadCountLoading,
            );

            return (
              <Popover
                key={type}
                open={openNotificationPopover === type}
                onOpenChange={(isOpen) => {
                  setOpenNotificationPopover(isOpen ? type : null);
                }}
              >
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="relative flex h-9 w-9 cursor-default items-center justify-center rounded-md transition-colors hover:bg-neutral-200 dark:hover:bg-white/10"
                    aria-label={label}
                  >
                    <Icon className="text-sidebar-foreground/70 h-4 w-4" />
                    {unreadCount > 0 ? (
                      <span className="absolute -top-1 -right-1 flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] leading-5 font-semibold text-white">
                        {formattedUnreadCount}
                      </span>
                    ) : null}
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  ref={notificationPopoverContentRef}
                  side="bottom"
                  align="end"
                  sideOffset={8}
                  collisionPadding={{ top: 40, bottom: 40 }}
                  className="flex h-[80vh] w-[520px] flex-col space-y-0 overflow-hidden p-0"
                  onInteractOutside={(event) => {
                    if (
                      isNotificationContextMenuOpen ||
                      (event.target instanceof Node &&
                        notificationPopoverContentRef.current?.contains(
                          event.target,
                        ))
                    ) {
                      event.preventDefault();
                    }
                  }}
                >
                  <div className="border-border flex h-14 items-center justify-between border-b px-4">
                    <Typography variant="heading" className="text-base">
                      {title}
                    </Typography>
                    {!unreadCountLoading && unreadCount > 0 ? (
                      <Typography
                        variant="subHeading"
                        className="text-muted-foreground text-sm"
                      >
                        {formattedUnreadCount} unread
                      </Typography>
                    ) : null}
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    <NotificationItems
                      category={type}
                      onContextMenuOpenChange={setIsNotificationContextMenuOpen}
                      onNotificationSelect={() => {
                        setOpenNotificationPopover(null);
                      }}
                    />
                  </div>
                </PopoverContent>
              </Popover>
            );
          },
        )}
        <a
          href="https://help.lawworks.com/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Open LawWorks help"
          className="relative flex h-9 w-9 cursor-default items-center justify-center rounded-md transition-colors hover:bg-neutral-200 dark:hover:bg-white/10"
        >
          <CircleHelp className="text-sidebar-foreground/70 h-4 w-4" />
        </a>
        <UserProfileDropdown forceCompact showIdentity />
      </div>
    </header>
  );
}
